import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ScanEye, ArrowUpRight, ArrowDownRight, Info, Sparkles } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip as RTooltip, CartesianGrid, LineChart, Line,
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => (n == null ? '—' : Number(n).toFixed(0));
const priorityColor = (p) =>
  p === 'high' ? 'border-rose-500/40 text-rose-700 bg-rose-500/10'
  : p === 'medium' ? 'border-amber-500/40 text-amber-700 bg-amber-500/10'
  : 'text-muted-foreground';

/** Public, unauthenticated view of a shared audit report. */
export default function PublicSharePage() {
  const { token } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/share/${token}`)
      .then((r) => setReport(r.data))
      .catch((e) => setError(e?.response?.status === 404 ? 'This share link is not active.' : 'Could not load report.'))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-8 max-w-5xl mx-auto space-y-4">
        {[0, 1, 2].map((i) => <Skeleton key={i} className="h-32" />)}
      </div>
    );
  }
  if (error || !report) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="p-8 max-w-md text-center border">
          <div className="font-display text-lg font-semibold">Report unavailable</div>
          <p className="text-sm text-muted-foreground mt-1">{error}</p>
          <a href="/" className="inline-flex items-center gap-1 mt-4 text-[hsl(var(--accent))] hover:underline text-sm">
            Go to GEOrush →
          </a>
        </Card>
      </div>
    );
  }

  const sub = report.sub_scores || {};
  const provs = report.provider_breakdown || {};
  const gen = (report.generated_at || '').split('T')[0];
  const totalPrompts = new Set((report.prompt_table || []).map((p) => p.prompt_id)).size;
  const active = report.active_providers || [];
  const project = report.project || {};
  const sovData = Object.entries(report.competitor_sov || {}).map(([name, count]) => ({
    name, count, isBrand: name === project.name,
  }));
  const trendData = (report.trend || []).map((t) => ({
    date: (t.generated_at || '').split('T')[0].slice(5),
    score: t.visibility_score,
  }));
  const weights = report.weights || {};

  return (
    <div className="min-h-screen bg-background">
      {/* Public header */}
      <header className="border-b bg-secondary/40 backdrop-blur">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2" data-testid="share-header-logo">
            <div className="h-8 w-8 rounded-lg bg-[hsl(var(--accent))] grid place-items-center">
              <ScanEye className="h-4 w-4 text-[hsl(var(--accent-foreground))]" />
            </div>
            <div className="leading-tight">
              <div className="font-display font-semibold text-sm">GEOrush</div>
              <div className="text-[10px] text-muted-foreground -mt-0.5">AEO Visibility</div>
            </div>
          </a>
          <a href="/pricing" className="text-xs text-[hsl(var(--accent))] hover:underline">Track your own AI visibility →</a>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8 space-y-4">
        {/* Headline */}
        <Card className="border p-6">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">AI visibility audit for</div>
          <div className="font-display text-2xl font-semibold mt-1">{project.name || 'Brand'}</div>
          <div className="flex items-baseline gap-3 mt-3">
            <div className="text-5xl font-display font-semibold text-[hsl(var(--accent))] tabular">{fmt(report.visibility_score)}</div>
            <div className="text-lg text-muted-foreground">/ 100</div>
            {report.delta_vs_previous != null && (
              <Badge variant="outline" className={`text-[11px] gap-1 ${report.delta_vs_previous >= 0 ? 'border-emerald-500/40 text-emerald-700 bg-emerald-500/10' : 'border-rose-500/40 text-rose-700 bg-rose-500/10'}`}>
                {report.delta_vs_previous >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                {report.delta_vs_previous >= 0 ? '+' : ''}{report.delta_vs_previous} vs last report
              </Badge>
            )}
          </div>
          <div className="text-xs text-muted-foreground mt-3 leading-relaxed max-w-2xl">
            This report is a snapshot generated on <span className="font-medium text-foreground">{gen}</span> from a scan of <span className="font-medium text-foreground">{totalPrompts} prompts</span> across <span className="font-medium text-foreground">{active.join(', ') || '—'}</span>. Scores can shift slightly between runs — AI responses aren't perfectly deterministic.
          </div>
          <div className="text-[11px] text-muted-foreground mt-3 flex items-center gap-1">
            <Info className="h-3 w-3" />
            Score = {Math.round((weights.mention_rate || 0.35) * 100)}% Mention · {Math.round((weights.citation_strength || 0.30) * 100)}% Citation · {Math.round((weights.share_of_voice || 0.20) * 100)}% SoV · {Math.round((weights.sentiment || 0.15) * 100)}% Sentiment
          </div>
        </Card>

        {/* Sub-scores */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { l: 'Mention Rate', v: sub.mention_rate, h: 'How often the brand appears' },
            { l: 'Citation Strength', v: sub.citation_strength, h: 'Mentions backed by a URL' },
            { l: 'Share of Voice', v: sub.share_of_voice, h: 'Brand slice vs competitors' },
            { l: 'Sentiment', v: sub.sentiment, h: 'Average tone when mentioned' },
          ].map((c) => (
            <Card key={c.l} className="border p-4">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">{c.l}</div>
              <div className="mt-1 text-3xl font-display font-semibold tabular">{fmt(c.v)}</div>
              <div className="text-[11px] text-muted-foreground mt-1">{c.h}</div>
            </Card>
          ))}
        </div>

        {/* Provider breakdown + SoV */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="border p-4">
            <div className="font-display text-sm font-semibold mb-3">Provider breakdown</div>
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-wide text-muted-foreground">
                <tr className="border-b"><th className="text-left py-2">Provider</th><th className="text-right">Score</th><th className="text-right">Mention</th><th className="text-right">Citation</th><th className="text-right">SoV</th><th className="text-right">Sentiment</th></tr>
              </thead>
              <tbody>
                {Object.entries(provs).map(([p, v]) => (
                  <tr key={p} className="border-b last:border-0">
                    <td className="py-2 capitalize">{p}</td>
                    <td className="text-right tabular font-semibold text-[hsl(var(--accent))]">{fmt(v.visibility_score)}</td>
                    <td className="text-right tabular">{fmt(v.mention_rate)}</td>
                    <td className="text-right tabular">{fmt(v.citation_strength)}</td>
                    <td className="text-right tabular">{fmt(v.share_of_voice)}</td>
                    <td className="text-right tabular">{fmt(v.sentiment)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
          <Card className="border p-4">
            <div className="font-display text-sm font-semibold mb-3">Share of voice</div>
            <div className="h-52">
              <ResponsiveContainer>
                <BarChart data={sovData}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="name" fontSize={11} />
                  <YAxis fontSize={11} />
                  <RTooltip />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]} fill="#94a3b8" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        {/* Recommendations */}
        {(report.recommendations || []).length > 0 && (
          <Card className="border p-4">
            <div className="font-display text-sm font-semibold mb-3">Recommendations</div>
            <ol className="space-y-3">
              {report.recommendations.map((rec, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Badge variant="outline" className={`text-[10px] shrink-0 mt-0.5 ${priorityColor(rec.priority)}`}>{rec.priority}</Badge>
                  <div>
                    <div className="text-sm font-medium">{rec.title}</div>
                    <div className="text-xs text-muted-foreground">{rec.description}</div>
                  </div>
                </li>
              ))}
            </ol>
          </Card>
        )}

        {trendData.length >= 2 && (
          <Card className="border p-4">
            <div className="font-display text-sm font-semibold mb-3">Score trend</div>
            <div className="h-56">
              <ResponsiveContainer>
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="date" fontSize={11} />
                  <YAxis fontSize={11} domain={[0, 100]} />
                  <RTooltip />
                  <Line type="monotone" dataKey="score" stroke="hsl(var(--accent))" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
        )}

        {/* Footer CTA (removed on Enterprise white-label — deferred) */}
        <div className="mt-8 rounded-xl border bg-secondary/40 p-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <div className="h-8 w-8 rounded-lg bg-[hsl(var(--accent))] grid place-items-center">
              <ScanEye className="h-4 w-4 text-[hsl(var(--accent-foreground))]" />
            </div>
            <div className="font-display font-semibold">GEOrush</div>
          </div>
          <div className="text-sm text-muted-foreground max-w-md mx-auto">Generated with GEOrush — track your own brand's AI visibility across ChatGPT, Claude, and Gemini.</div>
          <a href="/pricing" className="inline-flex items-center gap-1.5 mt-3 rounded-full bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] px-4 py-1.5 text-sm font-medium hover:opacity-90 transition">
            <Sparkles className="h-3.5 w-3.5" /> Start tracking my brand
          </a>
        </div>
      </main>
    </div>
  );
}
